<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router'

const router = useRouter()
const { individualId } = useRoute().params as { individualId: string }

const viewConnections = () => {
  router.push({
    name: 'connecting.individuals',
    params: { slug: 'connections', individualId },
  })
}
</script>

<template>
  <div
    class="mx-auto flex min-h-[calc(100vh-100px)] max-w-[640px] flex-col items-center justify-center px-6 py-10 text-center"
  >
    <div class="icon-wrap relative mb-9 h-[160px] w-[160px]">
      <!-- Exponential gaps from center outward: 8px, 16px, 32px -->
      <div class="ring-outer absolute inset-0 rounded-full border-[1.5px] border-[rgba(0,132,180,0.08)]" />
      <div class="ring-middle absolute inset-[32px] rounded-full border-[1.5px] border-[rgba(0,145,146,0.15)]" />
      <div class="ring-inner absolute inset-[48px] rounded-full border-[1.5px] border-[rgba(0,145,146,0.28)]" />
      <div
        class="check-circle absolute inset-[56px] flex items-center justify-center rounded-full shadow-[0_6px_24px_rgba(0,145,146,0.3)]"
      >
        <svg
          width="22"
          height="22"
          fill="none"
          stroke="white"
          stroke-width="2.5"
          viewBox="0 0 24 24"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
    </div>

    <p class="eyebrow mb-3 text-[13px] font-semibold uppercase tracking-[0.1em] text-[#009192]">You're all set</p>
    <h2 class="title mb-4 text-[32px] font-bold leading-[1.2] tracking-tight text-gray-900">
      We're connecting you with <span class="gradient-text">top firms</span>
    </h2>
    <p class="subtitle mx-auto mb-10 max-w-[380px] text-[15px] leading-[1.7] text-gray-500">
      Firms will review your profile and approve connections. You'll see them here once approved &mdash; we'll also send
      you an email.
    </p>

    <button
      class="cta inline-flex cursor-pointer items-center gap-2.5 rounded-xl border-none px-8 py-3.5 font-[inherit] text-sm font-semibold text-white shadow-[0_4px_20px_rgba(0,145,146,0.2)] transition-all duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 hover:shadow-[0_6px_28px_rgba(0,145,146,0.35)] active:scale-[0.97]"
      @click="viewConnections"
    >
      View My Connections
      <svg
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        viewBox="0 0 24 24"
        stroke-linecap="round"
        stroke-linejoin="round"
        width="16"
        height="16"
      >
        <path d="M5 12h14m-6-6l6 6-6 6" />
      </svg>
    </button>
  </div>
</template>

<style scoped>
/* ─── Gradient backgrounds ─── */
.check-circle {
  background: linear-gradient(135deg, #009192, #0084b4);
}

.gradient-text {
  background: linear-gradient(90deg, #009192, #0084b4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.cta {
  background: linear-gradient(135deg, #009192, #0084b4);
}

/* ─── Animations ─── */
.ring-outer {
  animation: scaleIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
}

.ring-middle {
  animation: scaleIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.18s both;
}

.ring-inner {
  animation: scaleIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.26s both;
}

.check-circle {
  animation: scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.35s both;
}

.eyebrow {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.45s both;
}

.title {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.55s both;
}

.subtitle {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.65s both;
}

.cta {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.75s both;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ring-outer,
  .ring-middle,
  .ring-inner,
  .check-circle,
  .eyebrow,
  .title,
  .subtitle,
  .cta {
    animation: none;
  }

  .cta {
    transition: none;
  }
}
</style>
